import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Chip from '../../componentsv2/Chip';
import Typography from '../../componentsv2/Typography';
import Button from '../../componentsv2/Button';
import Table from '../../componentsv2/Table';
import { APIStakeholder } from '../../types/documents';
import { AddIcon, TrashIcon, EditIcon, EyeIcon } from '../../components/Icons';
import { useTranslation } from '../../hooks/i18n';
import { useState } from 'react';
import { Nullable } from '../../types';
import Modal from '../../componentsv2/Modal';
import {
  addCompanyShareholder,
  deleteCompanyShareholder,
  editCompanyShareholder,
  updateShareholderAsset,
} from '../../services/documents';
import { statusColorsMap } from './utils';
import ErrorMessage from '../../components/ErrorMessage';
import { logEvent } from '@amplitude/analytics-browser';
import { docsCollectionEvents } from '../../constants/events';
import { TextField } from '../../componentsv2/Field';
import AddEditStakeholderDrawer from './AddEditStakeholder';
import Tooltip from '../../componentsv2/Tooltip';
import { PERSON_TYPE } from '../../constants/data';

function getStatus(shareholders: APIStakeholder[]): string {
  if (!shareholders.length) return 'PENDING';
  const status = shareholders[0].status;
  if (!!shareholders[0].rejectComment && status === 'PENDING') return 'DENIED';
  return status;
}

const Shareholders = ({
  shareholders,
  companyId,
  loadShareholders,
  isAdmin = false,
  language = 'en',
  onApprove = () => Promise.resolve(),
  onReject = () => Promise.resolve(),
}: {
  shareholders: APIStakeholder[];
  companyId: number;
  loadShareholders: (companyId: number) => Promise<void>;
  isAdmin?: boolean;
  language?: string;
  onApprove?: (companyId: number, categoryId: number) => Promise<void>;
  onReject?: (companyId: number, categoryId: number, rejectionComment: string) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [showShareholdersModal, setShowShareholdersModal] = useState(false);
  const [currentShareholder, setCurrentShareholder] = useState<Nullable<APIStakeholder>>(null);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [showRejectionConfirmationModal, setShowRejectionConfirmationModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [currentParent, setCurrentParent] = useState<Nullable<APIStakeholder>>(null);
  const [isLoading, setIsLoading] = useState(false);

  const findParent = (shareholder: APIStakeholder) =>
    shareholders.find(({ id }) => id === shareholder.parentId) ?? null;

  const categoryId = shareholders.length ? shareholders[0].categoryId : null;
  const status = getStatus(shareholders);

  const openShareholderModal = (shareholder?: APIStakeholder) => {
    shareholder && setCurrentShareholder(shareholder);
    setCurrentParent(shareholder ? findParent(shareholder) : null);
    setShowShareholdersModal(true);
  };

  // Adding a shareholder to a juridical one continues its ownership chain.
  const openNestedShareholderModal = (parent: APIStakeholder) => {
    setCurrentShareholder(null);
    setCurrentParent(parent);
    setShowShareholdersModal(true);
  };

  const closeShareholderModal = () => {
    loadShareholders(companyId);
    setCurrentShareholder(null);
    setCurrentParent(null);
    setShowShareholdersModal(false);
  };

  const openConfirmationModal = (shareholder?: APIStakeholder) => {
    shareholder && setCurrentShareholder(shareholder);
    setShowDeleteConfirmationModal(true);
  };

  const closeConfirmationModal = () => {
    setCurrentShareholder(null);
    setShowDeleteConfirmationModal(false);
  };

  return (
    <>
      <Modal
        isOpen={showRejectionConfirmationModal}
        handleClose={() => {
          setShowRejectionConfirmationModal(false);
          setRejectionReason('');
        }}
      >
        <Grid container>
          <Grid item xs={12}>
            <Typography>
              Please add a rejection reason (in {language === 'es' ? 'spanish' : 'english'})
            </Typography>
            <TextField
              multiline
              minRows={4}
              name="reason"
              value={rejectionReason}
              onChange={(value) => {
                setRejectionReason(value);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ gap: '4px', display: 'flex', justifyContent: 'center' }}>
              <Button
                color="danger"
                onClick={() => {
                  if (rejectionReason && !!categoryId) {
                    setIsLoading(true);
                    onReject(companyId, categoryId, rejectionReason).finally(() => {
                      setIsLoading(false);
                    });
                    setRejectionReason('');
                    setShowRejectionConfirmationModal(false);
                  } else {
                    alert('Please add a rejection reason');
                  }
                }}
              >
                Confirm
              </Button>
              <Button
                type="outline"
                color="danger"
                onClick={() => {
                  setRejectionReason('');
                  setShowRejectionConfirmationModal(false);
                }}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Modal>
      {showShareholdersModal && (
        <AddEditStakeholderDrawer
          isAdmin={isAdmin}
          stakeholder={currentShareholder}
          companyId={companyId}
          parentName={currentParent?.fullName}
          onAddStakeholder={async (companyId, values, files) => {
            await addCompanyShareholder(companyId, values, files, isAdmin, currentParent?.id).then(() => {
              closeShareholderModal();
            });
          }}
          onEditStakeholder={async (companyId, shareholderId, values) => {
            await editCompanyShareholder(companyId, shareholderId, values, isAdmin).then(() => {
              closeShareholderModal();
            });
          }}
          onUpdateStakeholderAsset={async (companyId, shareholderId, assetId, values) => {
            await updateShareholderAsset(companyId, shareholderId, assetId, values, isAdmin);
          }}
          handleClose={() => closeShareholderModal()}
          showDrawer={showShareholdersModal}
          isAccepted={status === 'ACCEPTED'}
          isShareholder
        />
      )}
      <Modal
        isOpen={showDeleteConfirmationModal}
        handleClose={() => {
          setShowDeleteConfirmationModal(false);
        }}
        size="small"
      >
        <Grid container>
          <Grid item xs={12}>
            {currentShareholder && (
              <Typography>
                {t('client:documents.shareholders.confirmDelete', { name: currentShareholder!.fullName })}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: '4px' }}>
              <Button
                color="danger"
                onClick={() => {
                  deleteCompanyShareholder(companyId, currentShareholder!.id, isAdmin).then(() => {
                    closeConfirmationModal();
                    logEvent(docsCollectionEvents.DELETE_SHAREHOLDER);
                    loadShareholders(companyId);
                  });
                }}
              >
                {t('client:documents.shareholders.confirmDeleteBtn')}
              </Button>
              <Button
                type="outline"
                color="danger"
                onClick={() => {
                  closeConfirmationModal();
                }}
              >
                {t('client:documents.shareholders.confirmDeleteCancel')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Modal>
      <Box
        sx={{
          boxShadow: '0px 10px 24px rgba(201, 210, 224, 0.3)',
          background: '#fbfcfe',
          borderRadius: '12px',
          padding: '2em',
          marginBottom: '1em',
        }}
      >
        <Grid container>
          <Grid item xs={12} md={6} sx={{ display: 'flex', gap: '2em', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '1.2em', fontWeight: 'bold' }}>
              {t('client:documents.shareholders.title')}
            </Typography>
            <Chip
              sx={{ ...statusColorsMap[status] }}
              label={t(`client:documents.status.${status}`)}
              size="small"
            />
            {status === 'DENIED' && <ErrorMessage errorMessage={shareholders[0].rejectComment as string} />}
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: 'flex', justifyContent: { xs: 'initial', md: 'flex-end' } }}
          >
            {isAdmin && (
              <>
                <Button
                  type="link"
                  disabled={!categoryId || isLoading}
                  color="danger"
                  onClick={() => {
                    setShowRejectionConfirmationModal(true);
                    logEvent(docsCollectionEvents.REJECT_DOC, { Name: 'SHAREHOLDERS' });
                  }}
                >
                  Reject
                </Button>
                <Button
                  type="link"
                  disabled={!categoryId || isLoading}
                  onClick={() => {
                    if (categoryId) {
                      if (
                        !window.confirm(
                          'Are you sure you want to approve Shareholders? This operation is not reversible.'
                        )
                      )
                        return;
                      setIsLoading(true);
                      onApprove(companyId, categoryId).finally(() => {
                        setIsLoading(false);
                      });
                      logEvent(docsCollectionEvents.APPROVE_DOC, { Name: 'SHAREHOLDERS' });
                    }
                  }}
                >
                  Approve
                </Button>
              </>
            )}
            <Box
              sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', paddingLeft: '2em' }}
            >
              <Button
                isFullHeight={false}
                type="outline"
                color="secondary"
                startIcon={<AddIcon />}
                disabled={status === 'ACCEPTED'}
                onClick={() => {
                  logEvent(docsCollectionEvents.OPEN_ADD_SHAREHOLDER_MODAL);
                  openShareholderModal();
                }}
              >
                {t('client:documents.buttons.add')}
              </Button>
            </Box>
          </Grid>
          {shareholders.length > 0 && (
            <Grid item xs={12}>
              <Table
                columns={[
                  {
                    field: 'fullName',
                    headerName: t('client:documents.shareholders.fullName.label'),
                  },
                  {
                    field: 'personType',
                    headerName: t('client:documents.shareholders.personType.label'),
                    renderCell: ({ personType }: APIStakeholder) => {
                      return <Typography>{t(`client:documents.shareholders.${personType}`)}</Typography>;
                    },
                  },
                  {
                    field: 'percentageShare',
                    headerName: t('client:documents.shareholders.percentageShare.label'),
                    renderCell: (shareholder) => <Typography>{shareholder.percentageShare}%</Typography>,
                  },
                  {
                    field: 'parentId',
                    headerName: t('client:documents.shareholders.ownerOf.label'),
                    renderCell: ({ parentId }: APIStakeholder) => (
                      <Typography>
                        {shareholders.find(({ id }) => id === parentId)?.fullName ??
                          t('client:documents.shareholders.ownerOf.company')}
                      </Typography>
                    ),
                  },
                  {
                    field: 'personIdNumber',
                    headerName: t('client:documents.shareholders.personIdNumber.label'),
                  },
                  {
                    field: 'email',
                    headerName: t('client:documents.shareholders.email.label'),
                  },
                  {
                    headerName: t('client:documents.shareholders.actions'),
                    renderCell: (shareholder) => {
                      return (
                        <>
                          <IconButton
                            onClick={() => {
                              logEvent(docsCollectionEvents.OPEN_EDIT_SHAREHOLDER_MODAL, {
                                Name: shareholder.fullName,
                              });
                              openShareholderModal(shareholder);
                            }}
                          >
                            {status === 'ACCEPTED' ? <EyeIcon /> : <EditIcon />}
                          </IconButton>
                          {shareholder.personType === PERSON_TYPE.JURIDICAL && (
                            <Tooltip title={t('client:documents.shareholders.addNestedTooltip')}>
                              <IconButton
                                disabled={status === 'ACCEPTED'}
                                onClick={() => openNestedShareholderModal(shareholder)}
                              >
                                <AddIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <IconButton
                            disabled={status === 'ACCEPTED'}
                            onClick={() => {
                              openConfirmationModal(shareholder);
                            }}
                          >
                            <TrashIcon />
                          </IconButton>
                        </>
                      );
                    },
                  },
                ]}
                data={shareholders}
              />
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
};

export default Shareholders;
